import { useEffect, useState } from "react";
import "./Navbar.css";
import { useWindowSize } from "@uidotdev/usehooks";
import { BREAKPOINTS } from "../../services/theme";
import Hamburger from "hamburger-react";
import gsap from "gsap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { myConfig } from "../../config";
import { logCTA } from "../../services/firebase/analytics";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../LanguageSwitcher";

export const Navbar = ({
  hideLinks = false,
  onHeightCalculated,
}: {
  hideLinks?: boolean;
  onHeightCalculated?: (height: number) => void;
}) => {
  const [isNavbarBlurred, setIsNavbarBlurred] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // Per prevenire spam
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Helper function to get the correct path with language prefix
  const getLocalizedPath = (hash: string) => {
    const currentPath = location.pathname;
    const isEnglish = currentPath.startsWith('/en');
    const basePath = isEnglish ? '/en' : '';
    return `${basePath}${hash}`;
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMenuOpen, isNavbarBlurred]);

  useEffect(() => {
    if (isMenuOpen && width && width > BREAKPOINTS.lg) {
      toggleMenu();
    }
  }, [width]);

  useEffect(() => {
    if (hideLinks) return;
    if (isMenuOpen) {
      gsap.fromTo(".collapse", { opacity: 0 }, { opacity: 1, duration: 0.5 });
      gsap.fromTo(
        ".nav-link-custom",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }
      );
      gsap.fromTo(
        ".highlighted-btn",
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.5, delay: 0.5 }
      );
    } else {
      gsap.fromTo(
        ".nav-link-custom",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }
      );
    }
  }, [isMenuOpen, hideLinks]);

  const handleScroll = () => {
    const shouldBeBlurred = window.scrollY > 10 || isMenuOpen;
    if (!isNavbarBlurred && shouldBeBlurred) setIsNavbarBlurred(true);
    else if (!shouldBeBlurred && isNavbarBlurred) setIsNavbarBlurred(false);
  };

  const toggleMenu = () => {
    if (isButtonDisabled) return; // Previeni interazione se il bottone è disabilitato

    setIsButtonDisabled(true);
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = isMenuOpen ? "auto" : "hidden";

    // Rendi il bottone di nuovo cliccabile dopo 700ms
    setTimeout(() => setIsButtonDisabled(false), 700);
  };

  const handleLinkClick = () => {
    if (isMenuOpen) toggleMenu();
  };

  return (
    <>
      <div
        onLoad={(e) =>
          onHeightCalculated && onHeightCalculated(e.currentTarget.clientHeight)
        }
        className={`
        my-nav-bar  navbar-trasparent
        fixed-top navbar-dark navbar-expand-lg 
        align-items-start align-items-lg-center justify-content-between d-flex py-4
        ${isNavbarBlurred ? "navbar-blur" : ""}`}
      >
        <Link className="navbar-brand nav-item p-0" to={getLocalizedPath("/#hero")}>
          <img src="/assets/logo.png" alt="logo" />
        </Link>

        <div
          className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`}
          id="collapseContentId"
        >
          {hideLinks ? (
            <ul className="nav-links m-0 list-unstyled d-flex flex-lg-row justify-content-lg-start"></ul>
          ) : (
            <ul className="nav-links m-0 list-unstyled d-flex flex-lg-row justify-content-lg-start">
              <li className="mx-3 nav-item">
                <Link
                  to={getLocalizedPath("/#stats")}
                  className="nav-link-custom"
                  onClick={handleLinkClick}
                >
                  {t('navigation.facts') || 'I dati'}
                </Link>
              </li>

              <li className="mx-3 nav-item">
                <Link
                  to={getLocalizedPath("/#about")}
                  className="nav-link-custom"
                  onClick={handleLinkClick}
                >
                  {t('navigation.aboutUs') || 'Chi siamo'}
                </Link>
              </li>

              <li className="mx-3 nav-item">
                <Link
                  to={getLocalizedPath("/#our-strength")}
                  className="nav-link-custom"
                  onClick={handleLinkClick}
                >
                  {t('navigation.ourStrength') || 'La nostra forza'}
                </Link>
              </li>

              <li className="mx-3 nav-item">
                <Link
                  to={getLocalizedPath("/#support")}
                  className="nav-link-custom"
                  onClick={handleLinkClick}
                >
                  {t('navigation.ourSupport') || 'Il nostro supporto'}
                </Link>
              </li>

              <li className="mx-3 nav-item">
                <Link
                  to={getLocalizedPath("/#projects")}
                  className="nav-link-custom"
                  onClick={handleLinkClick}
                >
                  {t('navigation.caseStudies') || 'Case Studies'}
                </Link>
              </li>
            </ul>
          )}

          <div className="d-flex align-items-center gap-3">
            <LanguageSwitcher />
            <div
              onClick={() => {
                navigate(getLocalizedPath("/#contact"));
                logCTA("navbar-contact", "navbar", {
                  page_path: location.pathname,
                  section_id: "contact",
                });

                if (isMenuOpen) toggleMenu();
              }}
              className="highlighted-btn btn shadow-lg my-rounded"
            >
              {t('buttons.contact') || 'Contatta'}
            </div>
          </div>
        </div>

        {hideLinks ? null : (
          <button
            className="navbar-toggler"
            type="button"
            aria-label="Toggle navigation"
            onClick={toggleMenu}
            disabled={isButtonDisabled}
          >
            <Hamburger toggle={toggleMenu} toggled={isMenuOpen} />
          </button>
        )}
      </div>
    </>
  );
};
